/**
 * Gallery Section
 * Использует переиспользуемые компоненты
 */
import React, { useState, useRef } from 'react';
import { Text, Icon, Image, Container } from '../components';

export default function GallerySection({ data, styles }) {
  const [lightboxImage, setLightboxImage] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const sliderRef = useRef(null);

  const sectionStyle = data?.style || 'grid';
  const columns = data?.columns || 3;
  const gap = data?.gap || 16;
  const rounded = data?.rounded !== false;
  const images = data?.images || [];

  // Responsive grid classes based on columns setting
  const getGridClasses = () => {
    switch (columns) {
      case 1:
        return 'grid-cols-1';
      case 2:
        return 'grid-cols-1 sm:grid-cols-2';
      case 3:
        return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';
      case 4:
        return 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4';
      default:
        return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';
    }
  };

  const scrollToSlide = (index) => {
    if (sliderRef.current) {
      const slideWidth = sliderRef.current.offsetWidth;
      sliderRef.current.scrollTo({
        left: index * slideWidth,
        behavior: 'smooth'
      });
      setCurrentSlide(index);
    }
  };

  const handleScroll = () => {
    if (sliderRef.current) {
      const slideWidth = sliderRef.current.offsetWidth;
      const scrollLeft = sliderRef.current.scrollLeft;
      const newSlide = Math.round(scrollLeft / slideWidth);
      if (newSlide !== currentSlide) {
        setCurrentSlide(newSlide);
      }
    }
  };

  const nextSlide = () => {
    if (currentSlide < images.length - 1) {
      scrollToSlide(currentSlide + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      scrollToSlide(currentSlide - 1);
    }
  };

  const ImageCard = ({ image, index }) => (
    <div
      className={`relative overflow-hidden cursor-pointer group aspect-[4/3] ${
        rounded ? 'rounded-lg md:rounded-xl' : ''
      }`}
      onClick={() => setLightboxImage(image)}
    >
      <Image
        src={image.src}
        alt={image.alt || `Изображение ${index + 1}`}
        fit="cover"
        className="w-full h-full transition-transform duration-300 group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
        <Icon
          name="ZoomIn"
          size={28}
          className="text-white opacity-0 group-hover:opacity-100 transition-opacity"
        />
      </div>
      {image.caption && (
        <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs md:text-sm p-2">
          {image.caption}
        </div>
      )}
    </div>
  );

  const renderGridStyle = () => (
    <div
      className={`grid ${getGridClasses()}`}
      style={{ gap: `${gap}px` }}
    >
      {images.map((image, index) => (
        <ImageCard key={image.id || index} image={image} index={index} />
      ))}
    </div>
  );

  const renderSliderStyle = () => (
    <div className="relative">
      {/* Slider container */}
      <div
        ref={sliderRef}
        onScroll={handleScroll}
        className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        {images.map((image, index) => (
          <div
            key={image.id || index}
            className="flex-shrink-0 w-full snap-center px-2 first:pl-0 last:pr-0"
          >
            <div
              className={`relative overflow-hidden cursor-pointer group aspect-[16/9] md:aspect-[21/9] ${
                rounded ? 'rounded-lg md:rounded-xl' : ''
              }`}
              onClick={() => setLightboxImage(image)}
            >
              <Image
                src={image.src}
                alt={image.alt || `Изображение ${index + 1}`}
                fit="cover"
                className="w-full h-full"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                <Icon
                  name="ZoomIn"
                  size={32}
                  className="text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg"
                />
              </div>
              {image.caption && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent text-white p-4 pt-8">
                  <p className="text-sm md:text-base">{image.caption}</p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Navigation arrows - desktop */}
      {images.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            disabled={currentSlide === 0}
            className="hidden md:flex absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full items-center justify-center shadow-lg transition-all disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <Icon name="ChevronLeft" size={24} />
          </button>
          <button
            onClick={nextSlide}
            disabled={currentSlide === images.length - 1}
            className="hidden md:flex absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full items-center justify-center shadow-lg transition-all disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <Icon name="ChevronRight" size={24} />
          </button>
        </>
      )}

      {/* Dots indicator */}
      {images.length > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => scrollToSlide(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentSlide
                  ? 'bg-primary w-6'
                  : 'bg-gray-300 hover:bg-gray-400'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );

  const renderCarouselStyle = () => (
    <div className="relative">
      {/* Carousel container - несколько изображений видны одновременно */}
      <div
        className="flex overflow-x-auto snap-x snap-mandatory gap-3 md:gap-4 pb-4"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        {images.map((image, index) => (
          <div
            key={image.id || index}
            className="flex-shrink-0 w-[75%] sm:w-[45%] md:w-[32%] lg:w-[24%] snap-start"
          >
            <div
              className={`relative overflow-hidden cursor-pointer group aspect-[4/3] ${
                rounded ? 'rounded-lg md:rounded-xl' : ''
              }`}
              onClick={() => setLightboxImage(image)}
            >
              <Image
                src={image.src}
                alt={image.alt || `Изображение ${index + 1}`}
                fit="cover"
                className="w-full h-full transition-transform duration-300 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                <Icon
                  name="ZoomIn"
                  size={24}
                  className="text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg"
                />
              </div>
              {image.caption && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent text-white p-2 pt-6">
                  <p className="text-xs md:text-sm truncate">{image.caption}</p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Подсказка для свайпа на мобильных */}
      {images.length > 3 && (
        <div className="flex md:hidden justify-center mt-2">
          <span className="text-xs text-text-secondary flex items-center gap-1">
            <Icon name="MoveHorizontal" size={14} />
            Листайте для просмотра
          </span>
        </div>
      )}
    </div>
  );

  return (
    <section
      style={{
        paddingTop: styles?.paddingTop || 64,
        paddingBottom: styles?.paddingBottom || 64,
        backgroundColor: styles?.backgroundColor || '#FFFFFF'
      }}
    >
      <Container>
        {/* Заголовок */}
        <Text
          as="h2"
          data={typeof data?.title === 'object' ? data.title : { text: data?.title }}
          defaults={{ size: '3xl', weight: 'bold', color: 'foreground' }}
          align="center"
          className={data?.subtitle ? 'mb-2 md:mb-3' : 'mb-6 md:mb-8'}
        />

        {/* Подзаголовок */}
        <Text
          as="p"
          data={typeof data?.subtitle === 'object' ? data.subtitle : { text: data?.subtitle }}
          defaults={{ size: 'lg', weight: 'normal', color: 'text-secondary' }}
          align="center"
          className="mb-6 md:mb-8"
        />

        {images.length > 0 ? (
          <>
            {sectionStyle === 'grid' && renderGridStyle()}
            {sectionStyle === 'slider' && renderSliderStyle()}
            {sectionStyle === 'carousel' && renderCarouselStyle()}
          </>
        ) : (
          <div className="text-center py-12 text-text-secondary">
            <Icon name="ImagePlus" size="3xl" className="mx-auto mb-4 opacity-50" />
            <p>Добавьте изображения в настройках</p>
          </div>
        )}
      </Container>

      {/* Лайтбокс */}
      {lightboxImage && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10"
            onClick={() => setLightboxImage(null)}
          >
            <Icon name="X" size={32} />
          </button>
          <img
            src={lightboxImage.src}
            alt={lightboxImage.alt}
            loading="eager"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
}
